<template>
  <v-dialog
    persistent
    lazy
    :value="show"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    :width="
      isTestBooking
        ? $store.state.common.largeDialogWidth
        : $store.state.common.extraLargeDialogWidth
    "
    @input="(val) => $emit('update:show', val)"
  >
    <v-card>
      <v-card-title>
        <v-layout
          align-center
          justify-center
        >
          <h1 class="headline">
            <span v-if="isTestBooking">{{
              $t('contentCreatorBookingDialogTitleTest')
            }}</span>
            <span v-else>{{ $t('contentCreatorBookingDialogTitle') }}</span>
          </h1>
          <v-btn
            icon
            :disabled="busy"
            :title="$t('abort')"
            class="my-0 ml-auto mr-0"
            @click="onAborted()"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-layout>
      </v-card-title>
      <v-card-text class="pt-1">
        <v-layout wrap>
          <!-- Show Plan Card with Price etc. only when not a Test Booking -->
          <v-flex
            v-if="!isTestBooking"
            xs12
            lg6
            class="px-3"
          >
            <v-layout
              justify-center
              align-center
              class="mb-2 mr-3"
            >
              <v-btn
                class="ma-0"
                :class="{
                  'msr--text': monthlyBillingChosen,
                  'ml-3': $vuetify.breakpoint.mdAndUp,
                }"
                :style="{
                  'text-decoration': monthlyBillingChosen
                    ? 'underline'
                    : 'initial',
                }"
                flat
                :large="$vuetify.breakpoint.mdAndUp"
                @click="setBillingPeriod('MONTHLY')"
              >
                {{ $t('contentCreatorPlanBillingMonthly') }}
              </v-btn>
              <v-btn
                class="ma-0"
                :class="{'msr--text': yearlyBillingChosen}"
                :style="{
                  'text-decoration': yearlyBillingChosen
                    ? 'underline'
                    : 'initial',
                }"
                flat
                :large="$vuetify.breakpoint.mdAndUp"
                @click="setBillingPeriod('YEARLY')"
              >
                {{ $t('contentCreatorPlanBillingYearly') }}
              </v-btn>
            </v-layout>
            <content-creator-plan-card
              v-if="plan"
              :billing-period="billingPeriod"
              :plan="plan"
              :show-name="showName"
              :show-test-period="isTestBooking"
              max-width="400px"
              no-left-margin
              no-right-margin
              hide-actions
              class="mx-auto mb-3"
            />
          </v-flex>
          <v-flex
            :xs12="!isTestBooking"
            :lg6="!isTestBooking"
            class="px-3"
          >
            <v-layout
              column
              :class="{'dialog-right-side-content': !isTestBooking}"
            >
              <div
                v-if="!busy && !loading && noWorkspacesAvailableForThisPlan"
                class="warning--text px-2 pb-2"
                style="font-size: larger"
              >
                {{
                  $t('contentCreatorBookingDialogWorkspaceSelectNotPossible')
                }}.
              </div>
              <div v-else>
                <!-- Select bookable Workspace -->
                <v-layout
                  v-if="loading && shouldHideWorkspaceSelectWidget"
                  justify-center
                >
                  <v-progress-circular
                    indeterminate
                    class="primary--text"
                  />
                </v-layout>
                <v-select
                  v-show="!shouldHideWorkspaceSelectWidget"
                  v-model="selectedBookableWorkspaceId"
                  :items="filteredBookableWorkspaces"
                  :label="$t('workspace')"
                  :hint="$t('contentCreatorBookingDialogWorkspaceSelectHint')"
                  persistent-hint
                  item-text="name"
                  item-value="id"
                  class="mb-4"
                  :class="{
                    'v-select-highlight-hint':
                      selectedBookableWorkspaceId === null,
                  }"
                />
                <!-- Billed Stores Info -->
                <v-slide-y-reverse-transition>
                  <div
                    v-if="plan && plan.is_billed_per_store && selectedWorkspace"
                    class="mt-1 mb-3"
                  >
                    <div>
                      {{ billedPerStoreHint }}
                      <span class="body-2 nowrap">
                        {{ selectedWorkspace.stores.length }} x
                        {{ plan.price_monthly | price }} =
                        {{
                          selectedWorkspace.stores.length * plan.price_monthly
                        }}
                        €
                      </span>
                      <div class="caption grey--text text--darken-2 mt-1">
                        <span
                          class="clickable"
                          @click="
                            expandBilledStoresList = !expandBilledStoresList
                          "
                        >
                          <v-icon
                            v-if="!expandBilledStoresList"
                            small
                          >
                            keyboard_arrow_down
                          </v-icon>
                          <v-icon
                            v-if="expandBilledStoresList"
                            small
                          >
                            keyboard_arrow_up
                          </v-icon>
                          {{ $t('contentCreatorBookingExpandStores') }}
                        </span>
                        <v-slide-x-reverse-transition>
                          <ul
                            v-if="expandBilledStoresList"
                            class="mt-1"
                            style="
                              list-style: none;
                              max-height: 360px;
                              overflow: auto;
                            "
                          >
                            <li
                              v-for="(store, storeIndex) in orderBy(
                                selectedWorkspace.stores,
                                ['city', 'name']
                              )"
                              :key="'store_' + store.id"
                              class="mt-2 nowrap"
                              :class="{'mt-2': storeIndex === 0}"
                            >
                              <v-icon
                                small
                                color="msr"
                              >
                                location_on
                              </v-icon>
                              <b>{{ store.city }}</b> {{ store.name }}
                              <span class="grey--text">{{
                                store.address
                              }}</span>
                            </li>
                          </ul>
                        </v-slide-x-reverse-transition>
                      </div>
                    </div>
                  </div>
                </v-slide-y-reverse-transition>
              </div>
              <!-- Disclaimer: I have understood the booking conditions -->
              <div
                v-if="!noWorkspacesAvailableForThisPlan"
                :class="{caption: $vuetify.breakpoint.xs}"
              >
                <span v-if="isTestBooking">
                  <p>{{ $t('contentCreatorBookingDialogDisclaimerTest') }}</p>
                </span>
                <span v-else>
                  <span v-if="monthlyBillingChosen">{{
                    $t('contentCreatorBookingDialogDisclaimerBookMonthly')
                  }}</span>
                  <span v-if="yearlyBillingChosen">{{
                    $t('contentCreatorBookingDialogDisclaimerBookYearly')
                  }}</span>
                </span>
                <v-checkbox
                  v-if="needsToBeAcknowledgedByUser"
                  v-model="acknowledged"
                  :disabled="busy"
                  color="msr"
                  class="acknowlede-checkbox"
                  :label="$t('contentCreatorBookingDialogCheckboxLabel')"
                  data-cy="contentcreator-booking-dialog-checkbox"
                />
              </div>
              <!-- Warning: You must request this booking from an Admin -->
              <div
                v-if="selectedWorkspace && mustRequestBookingFromAdmin"
                :class="{caption: $vuetify.breakpoint.xs}"
              >
                <vue-markdown
                  :source="
                    $t('contentCreatorBookingDialogAdminConfirmationWarning') +
                      ': '
                  "
                />
                <span v-if="selectedWorkspace.manager">
                  <span
                    class="grey--text text--darken-2"
                  >{{ $t('workspaceManager') }}:</span>
                  <span class="msr--text nowrap">
                    {{ selectedWorkspace.manager.first_name }}
                    {{ selectedWorkspace.manager.last_name }}</span>

                  <span v-if="selectedWorkspace.coadmins.length > 0">
                    <br>
                    <span
                      class="grey--text text--darken-2"
                    >{{ $t('coadmins') }}:</span>
                    <span
                      v-for="(
                        coadmin, coadminIndex
                      ) of selectedWorkspace.coadmins"
                      :key="coadminIndex"
                      class="msr--text nowrap"
                    ><span v-if="coadmin.first_name || coadmin.last_name">{{
                      coadmin.first_name + ' ' + coadmin.last_name
                    }}</span><span v-else>{{ coadmin.email }}</span><span
                      v-if="
                        coadminIndex < selectedWorkspace.coadmins.length - 1
                      "
                    >,&nbsp;</span>
                    </span>
                  </span>
                </span>
                <span
                  v-else
                  class="warning--text"
                >
                  {{ $t('contentCreatorBookingDialogBookingNotPossible') }}
                </span>
              </div>
              <v-layout
                justify-center
                :wrap="$vuetify.breakpoint.mdAndDown"
                class="mb-2 grow-0 mt-auto"
              >
                <btn-ok
                  v-if="!noWorkspacesAvailableForThisPlan"
                  class="mt-2 mx-1"
                  :disabled="!formIsValid"
                  :loading="busy"
                  label-with-icon
                  :label="confirmationButtonLabel"
                  icon="check"
                  round
                  data-cy="contentcreator-booking-dialog-confirm-btn"
                  @click="onConfirmed()"
                />
              </v-layout>
            </v-layout>
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import orderBy from 'lodash.orderby';
import VueMarkdown from 'vue-markdown';

import BtnOk from '@/components/ButtonOk';

import ContentCreatorPlanCard from '@/components/ContentCreatorPlanCard';

export default {
  components: {
    BtnOk,
    ContentCreatorPlanCard,
    VueMarkdown,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    plan: {
      type: Object,
      default: null,
    },
    isTestBooking: {
      type: Boolean,
      required: true,
    },
    showName: {
      type: Boolean,
      default: false,
    },
    billingPeriod: {
      type: String,
      default: 'MONTHLY',
      validate: function (period) {
        return ['MONTHLY', 'YEARLY'].includes(period);
      },
    },
    workspaceFilter: {
      type: Function,
      required: true,
    },
    busy: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      bookableWorkspaces: [],
      selectedBookableWorkspaceId: null,
      acknowledged: false,
      expandBilledStoresList: false,
    };
  },
  computed: {
    needsToBeAcknowledgedByUser() {
      if (this.isTestBooking) {
        return false;
      }
      if (
        this.$store.getters.userIsBeingManaged &&
        !this.$store.getters.isManagedCoadmin
      ) {
        return false;
      }
      return true;
    },
    monthlyBillingChosen() {
      return this.billingPeriod === 'MONTHLY';
    },
    yearlyBillingChosen() {
      return this.billingPeriod === 'YEARLY';
    },
    confirmationButtonLabel() {
      if (this.isTestBooking) {
        return this.$t('contentCreatorBookingDialogButtonTest');
      }
      if (this.mustRequestBookingFromAdmin) {
        return this.$t('contentCreatorBookingDialogButtonRequest');
      }
      return this.$t('contentCreatorBookingDialogButtonBook');
    },
    shouldHideWorkspaceSelectWidget() {
      if (this.loading) {
        return true;
      }
      if (this.filteredBookableWorkspaces.length === 1) {
        const onlyWorkspace = this.filteredBookableWorkspaces[0];
        return !onlyWorkspace.contains_association_groups;
      }
      return false;
    },
    billedPerStoreHint() {
      if (!this.selectedWorkspace) {
        return '';
      }
      const numStores = this.selectedWorkspace.stores.length;
      if (numStores === 1) {
        return this.$t('contentCreatorBookingDialogBilledPerStoreHintSingle');
      }
      const template = this.$t('contentCreatorBookingDialogBilledPerStoreHint');
      return template.replace('{{ num }}', numStores);
    },
    filteredBookableWorkspaces() {
      if (!this.workspaceFilter) {
        return this.bookableWorkspaces;
      }
      return this.workspaceFilter(this.bookableWorkspaces);
    },
    selectedWorkspace() {
      if (
        !this.selectedBookableWorkspaceId ||
        !this.filteredBookableWorkspaces.length
      ) {
        return null;
      }
      return this.filteredBookableWorkspaces.filter(
        (workspace) => workspace.id === this.selectedBookableWorkspaceId
      )[0];
    },
    noWorkspacesAvailableForThisPlan() {
      return this.filteredBookableWorkspaces.length === 0;
    },
    mustRequestBookingFromAdmin() {
      if (this.isTestBooking) {
        return false;
      }
      if (!this.selectedWorkspace) {
        return false;
      }
      return !this.selectedWorkspace.user_can_book;
    },
    thereIsNoAdminToRequestBookingFrom() {
      if (!this.selectedWorkspace) {
        return false;
      }
      return (
        !this.selectedWorkspace.manager &&
        this.selectedWorkspace.coadmins.length == 0
      );
    },
    formIsValid() {
      const acknowledged = this.needsToBeAcknowledgedByUser
        ? this.acknowledged
        : true;

      return (
        this.selectedBookableWorkspaceId !== null &&
        (this.isTestBooking || acknowledged) &&
        (!this.mustRequestBookingFromAdmin ||
          !this.thereIsNoAdminToRequestBookingFrom)
      );
    },
  },
  watch: {
    show: function (show) {
      if (show) {
        this.fetchWhatIsBookable().then(() => {
          this.maybePreselectWorkspaceToBook();
        });
      }
    },
    selectedWorkspace: function () {
      if (this.selectedWorkspace && this.selectedWorkspace.stores.length <= 6) {
        this.expandBilledStoresList = true;
      }
    },
  },
  methods: {
    orderBy: orderBy,
    setBillingPeriod(period) {
      if (this.billingPeriod !== period && this.acknowledged) {
        this.acknowledged = false;
      }
      this.$emit('update:billingPeriod', period);
    },
    fetchWhatIsBookable() {
      this.loading = true;
      return this.$api
        .contentCreatorWhatIsBookable()
        .then((response) => {
          this.bookableWorkspaces = response.data.workspaces;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    maybePreselectWorkspaceToBook() {
      const onlyOne = this.filteredBookableWorkspaces.length === 1;
      if (onlyOne) {
        this.selectedBookableWorkspaceId =
          this.filteredBookableWorkspaces[0].id;
      }
    },
    reset() {
      this.selectedBookableWorkspaceId = null;
      this.acknowledged = false;
    },
    closeDialog() {
      this.reset();
      this.$emit('update:show', false);
    },
    onAborted() {
      this.closeDialog();
      this.$emit('aborted');
    },
    onConfirmed() {
      this.$emit('confirmed', {
        // What Workspace and Plan have been selected.
        billingPeriod: this.billingPeriod,
        isTest: this.isTestBooking,
        planId: this.plan.id,
        workspaceId: this.selectedBookableWorkspaceId,
        // Some metadata about the selected Workspace.
        manager: this.selectedWorkspace.manager,
        coadmins: this.selectedWorkspace.coadmins,
        mustRequestBookingFromAdmin: this.mustRequestBookingFromAdmin,
      });
    },
  },
};
</script>

<style scoped>
.acknowlede-checkbox >>> .v-input__slot label {
  color: rgba(0, 0, 0, 0.87);
}

.dialog-right-side-content {
  margin-top: 48px;
  height: calc(100% - 48px);
}
</style>



// WEBPACK FOOTER //
// src/components/ContentCreatorPlanBookingDialog.vue